import { useCallback, useEffect, useRef, useState } from "react";

import { AuditActionSchema } from "@canvaschool/contracts";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";

import { formatDateTime } from "./admin/shared";

import type { ApiErrorCode } from "../api/client";
import type { AdminAuditEvent, AuditAction } from "@canvaschool/contracts";

const ACTION_LABEL: Record<AuditAction, string> = {
  ADMIN_CREATED: "관리자 계정 생성",
  TEACHER_CREATED: "교사 계정 생성",
  TEACHER_STATUS_CHANGED: "교사 상태 변경",
  PASSWORD_CHANGED: "비밀번호 변경",
  PASSWORD_RESET: "비밀번호 초기화",
  SESSION_REVOKED: "로그인 해제",
  CLASS_CREATED: "학급 생성",
  CLASS_RENAMED: "학급 이름 변경",
  CLASS_ARCHIVED: "학급 보관",
  CLASS_REACTIVATED: "학급 다시 운영",
  STUDENT_NAME_SET: "학생 이름 입력",
  STUDENT_NAME_CHANGED: "학생 이름 변경",
  STUDENT_NAME_CLEARED: "학생 이름 지움",
  STUDENT_STATUS_CHANGED: "학생 상태 변경",
  CANVAS_CREATED: "캔버스 생성",
  CANVAS_RENAMED: "캔버스 이름 변경",
  CANVAS_TRASHED: "캔버스 휴지통 이동",
  CANVAS_RESTORED: "캔버스 복원",
  REVISION_RESTORED: "이전 버전 복원",
  PURGE_STARTED: "영구 삭제 시작",
  PURGE_COMPLETED: "영구 삭제 완료",
  PURGE_FAILED: "영구 삭제 실패",
  BETA_APPLICATION_REJECTED: "베타 신청 거절",
  BETA_APPLICATION_DELETED: "베타 신청 삭제",
  TEACHER_SCHOOL_SET: "교사 학교 지정",
  GALLERY_POST_REMOVED: "갤러리 게시물 교사 내림",
  GALLERY_POST_ALLOWED_AGAIN: "갤러리 다시 허용",
  GALLERY_POST_HIDDEN: "갤러리 게시물 신고로 숨김",
  GALLERY_POST_RESTORED: "갤러리 게시물 다시 보이기",
  GALLERY_POST_DELETED: "갤러리 게시물 삭제",
  IMPERSONATION_STARTED: "교사 화면 들어감",
  IMPERSONATION_ENDED: "교사 화면 나옴",
};

const VALUE_LABEL: Record<string, string> = {
  ACTIVE: "사용 중",
  DISABLED: "중지",
  ARCHIVED: "보관",
};

const TARGET_LABEL: Record<string, string> = {
  user: "사용자",
  session: "세션",
  class: "학급",
  canvas: "캔버스",
  student_profile: "학생",
  beta_application: "베타 신청",
};

export const targetLabel = (targetType: string): string =>
  TARGET_LABEL[targetType] ?? targetType;

export const actionLabel = (action: string): string =>
  ACTION_LABEL[action as AuditAction] ?? action;

/** Class and canvas details stay out of the administrator's view. */
const HIDDEN_METADATA = /^(CLASS_|CANVAS_|STUDENT_NAME_)/;

export const describeMetadata = (event: AdminAuditEvent): string => {
  if (HIDDEN_METADATA.test(event.action)) {
    return "";
  }
  const { from, to } = event.metadata;
  if (
    event.action === "STUDENT_STATUS_CHANGED" &&
    typeof from === "boolean" &&
    typeof to === "boolean"
  ) {
    const label = (enabled: boolean) => (enabled ? "사용" : "중지");
    return `${label(from)} → ${label(to)}`;
  }
  return Object.entries(event.metadata)
    .map(([key, value]) => {
      const text = typeof value === "string" ? value : JSON.stringify(value);
      if (key === "to") {
        return `→ ${VALUE_LABEL[text] ?? text}`;
      }
      return `${key}: ${VALUE_LABEL[text] ?? text}`;
    })
    .join(", ");
};

/** Who did what to which account, newest first. No class or canvas content. */
export const AdminAuditPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [events, setEvents] = useState<AdminAuditEvent[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [action, setAction] = useState<AuditAction | "">("");
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [busy, setBusy] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(
    async (cursor?: string) => {
      const ticket = ++requestRef.current;
      setError(null);
      setBusy(true);
      try {
        const result = await api.listAuditEvents({
          action: action || undefined,
          cursor,
        });
        if (ticket === requestRef.current) {
          setEvents((current) =>
            cursor ? [...(current ?? []), ...result.items] : result.items,
          );
          setNextCursor(result.nextCursor);
        }
      } catch (failure) {
        if (ticket === requestRef.current) {
          setError(auth.handleError(failure));
        }
      } finally {
        if (ticket === requestRef.current) {
          setBusy(false);
        }
      }
    },
    [api, auth, action],
  );

  useEffect(() => {
    void load();
    // Reloading per filter; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, action]);

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>감사 기록</h1>
          <p className="canvaschool-page-lead">
            계정에 일어난 일을 최신순으로 보여 줍니다.
          </p>
        </div>
      </div>
      <div className="canvaschool-inline-form">
        <label htmlFor="canvaschool-audit-action">행동 종류</label>
        <select
          id="canvaschool-audit-action"
          value={action}
          onChange={(event) =>
            setAction(event.target.value as AuditAction | "")
          }
        >
          <option value="">전체</option>
          {AuditActionSchema.options.map((value) => (
            <option key={value} value={value}>
              {actionLabel(value)}
            </option>
          ))}
        </select>
      </div>
      {error && <ErrorAlert code={error} />}
      {events && events.length === 0 && (
        <p className="canvaschool-hint">기록이 없습니다.</p>
      )}
      {events && events.length > 0 && (
        <div className="canvaschool-table-wrap">
          <table className="canvaschool-table">
            <thead>
              <tr>
                <th scope="col">시각</th>
                <th scope="col">행동</th>
                <th scope="col">한 사람</th>
                <th scope="col">대상</th>
                <th scope="col">세부</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{formatDateTime(event.createdAt)}</td>
                  <td>{actionLabel(event.action)}</td>
                  <td>{event.actorLoginId ?? "시스템"}</td>
                  <td>
                    {event.targetLoginId ?? targetLabel(event.targetType)}
                  </td>
                  <td>{describeMetadata(event)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && (
        <button
          type="button"
          onClick={() => void load(nextCursor)}
          disabled={busy}
        >
          더 보기
        </button>
      )}
    </section>
  );
};
